import { Map as MapLibreMap, NavigationControl, GeolocateControl, ScaleControl, AttributionControl } from 'maplibre-gl';
import { createRadar } from './map/radar.js';
import { createMeteoPanel } from './ui/meteoPanel.js';
import { createRelief3d } from './map/relief3d.js';
import { buildStyle } from './map/style.js';
import { createFilterPanel } from './ui/filterPanel.js';
import { createFavoritesPanel } from './ui/favoritesPanel.js';
import { createRecents } from './ui/recents.js';
import { createTracksPanel } from './ui/tracksPanel.js';
import { createRoutePanel } from './ui/routePanel.js';
import { createNavOverlay } from './ui/navOverlay.js';
import { createSearchBox } from './ui/searchBox.js';
import { createAccount } from './ui/account.js';
import { createInfoPopup } from './map/popup.js';
import { backend } from './data/backend.js';
import { toast } from './ui/toast.js';
import { ortungVorbereiten, zurueckTaste } from './data/native.js';
import './styles/app.css';

const DEFAULT_VIEW = { center: [8.2275, 46.8], zoom: 7.4 };

/**
 * Soweit die Karte traegt - weiter nicht.
 *
 * Die Ausdehnung stammt aus der Reliefquelle selbst (tiles.json von
 * ch.swisstopo.relief.vt: 3.57..13.66 Ost, 44.18..48.88 Nord). Jenseits davon
 * gibt es keine Schummerung mehr, und ohne sie sieht die Karte aus, als waere
 * etwas kaputt. Lieber gar nicht erst hinlassen.
 */
const AUSDEHNUNG = [[3.57, 44.18], [13.66, 48.88]];

/* ------------------------------------------------------- Ansicht in der URL */

function viewFromHash() {
  const m = location.hash.match(/#map=([\d.]+)\/(-?[\d.]+)\/(-?[\d.]+)/);
  if (!m) return null;
  const [, z, lat, lng] = m;
  return { center: [Number(lng), Number(lat)], zoom: Number(z) };
}

function writeHash(map) {
  const c = map.getCenter();
  const hash = `#map=${map.getZoom().toFixed(2)}/${c.lat.toFixed(5)}/${c.lng.toFixed(5)}`;
  history.replaceState(null, '', hash);
}

/* ------------------------------------------------------------------- Karte */

const view = viewFromHash() || DEFAULT_VIEW;

const map = new MapLibreMap({
  container: 'map',
  style: buildStyle(),
  center: view.center,
  zoom: view.zoom,
  maxZoom: 19,
  maxBounds: AUSDEHNUNG,
  hash: false,
  attributionControl: false,
  dragRotate: false,
  pitchWithRotate: false
});

map.touchZoomRotate.disableRotation();
/* Zoom und Standort oben rechts, am Rand der Karte - dort liegt der Daumen
   nicht ueber dem, was man gerade ansieht. Unten links die Herkunft der
   Daten, der Massstab eine Zeile darueber. */
map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
map.addControl(new GeolocateControl({ trackUserLocation: true, positionOptions: { enableHighAccuracy: true } }), 'top-right');
map.addControl(new ScaleControl({ maxWidth: 110, unit: 'metric' }), 'bottom-left');
map.addControl(new AttributionControl({ compact: true }), 'bottom-left');

map.on('moveend', () => writeHash(map));

// Im Entwicklungsmodus greifbar machen - erleichtert das Nachsehen in der Konsole
if (import.meta.env?.DEV) window.map = map;

/* ---------------------------------------------------------- Kopfzeile / UI */

const el = (id) => document.getElementById(id);

const panel = el('panel');
const backdrop = el('panel-backdrop');
const sidebar = el('sidebar');
const sidebarBtn = el('btn-sidebar');
const infoTafel = el('info');

/** Schmales Fenster: das Panel liegt über der Karte statt daneben. */
const compact = () => window.matchMedia('(max-width: 860px)').matches;

/* Rechts wird bedient, links wird eingestellt.
 *
 * Die Schiene am rechten Rand steht immer; daneben faehrt die Spalte aus, in
 * der genau eines liegt: Auskunft, Route oder Favoriten. Die Darstellung -
 * Hintergrund, Ebenen, Punkte - oeffnet links und ist zu, solange niemand
 * danach fragt. So gehoert die Mitte der Karte.
 */
const filterBtn = el('btn-filters');
const routeBtn = el('btn-route');
const favBtn = el('btn-favorites');
const tourBtn = el('btn-tracks');
const meteoBtn = el('btn-meteo');
const railButtons = [routeBtn, favBtn, tourBtn, meteoBtn, filterBtn];

/** Wird weiter unten gesetzt; die Knoepfe laufen erst nach dem Aufbau. */
let info = null;

function setPanel(open) {
  panel.hidden = !open;
  backdrop.hidden = !open || !compact();
  filterBtn.classList.toggle('is-active', open);
  filterBtn.setAttribute('aria-pressed', String(open));
  map.resize();
}

function setSidebar(open) {
  sidebar.classList.toggle('is-zu', !open);
  sidebarBtn.setAttribute('aria-pressed', String(open));
  map.resize();
}

/** Nach einer Auswahl soll auf dem Telefon wieder die Karte zu sehen sein. */
function collapseOnCompact() {
  if (compact()) setPanel(false);
}

/** Was gerade in der rechten Spalte steht - oder nichts. */
const offeneAnsicht = () => {
  if (infoTafel.hidden) return null;
  if (!el('view-route').hidden) return 'route';
  if (!el('view-favorites').hidden) return 'favoriten';
  if (!el('view-tracks').hidden) return 'touren';
  if (!el('view-meteo').hidden) return 'meteo';
  return 'auskunft';
};

function showView(name) {
  if (name === 'filters') {
    setPanel(panel.hidden);
    return;
  }
  // Nochmals auf denselben Knopf: die Spalte wieder zu. Nur nicht bei den
  // Touren, solange aufgezeichnet wird - die Zahlen sollen stehen bleiben.
  const jetzt = offeneAnsicht();
  if ((name === 'route' && jetzt === 'route')
    || (name === 'favorites' && jetzt === 'favoriten')
    || (name === 'tracks' && jetzt === 'touren' && !tracks.laeuft())
    || (name === 'meteo' && jetzt === 'meteo')) {
    info?.schliessen();
    return;
  }
  if (name === 'route') info?.routeZeigen();
  else if (name === 'favorites') info?.favoritenZeigen();
  else if (name === 'tracks') info?.tourenZeigen();
  else if (name === 'meteo') {
    info?.meteoZeigen();
    // Beim ersten Mal den nächstgelegenen Prognosepunkt nehmen - eine leere
    // Spalte mit einem Suchfeld sagt niemandem, was dahintersteckt.
    ortsprognose.zeigen();
  }
  // Auf dem Telefon liegt das Panel ueber der Karte - es muss weg, sonst
  // sieht man von der Route nichts.
  collapseOnCompact();
}

for (const b of railButtons) {
  b.addEventListener('click', () => showView(b.dataset.view));
}
sidebarBtn.addEventListener('click', () => setSidebar(sidebar.classList.contains('is-zu')));

// Die Darstellung bleibt zu, bis jemand sie aufmacht - die Karte zuerst.
setPanel(false);
setSidebar(true);

backdrop.addEventListener('click', () => setPanel(false));
el('panel-close').addEventListener('click', () => setPanel(false));
map.on('click', collapseOnCompact);
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !panel.hidden && compact()) setPanel(false);
});

el('btn-share').addEventListener('click', async () => {
  writeHash(map);
  try {
    await navigator.clipboard.writeText(location.href);
    toast('Link zur Ansicht kopiert.');
  } catch {
    toast(location.href);
  }
});

/* --------------------------------------------------- Konto, Favoriten, Filter */

const account = createAccount({
  button: el('btn-account'),
  onChange: () => {
    favorites.load();
    loadPrefs();
  }
});

const favorites = createFavoritesPanel({
  map,
  host: el('fav-list'),
  badge: el('fav-count'),
  modeEl: el('fav-mode'),
  backend,
  getProfile: () => account.getProfile(),
  onNavigate: collapseOnCompact,
  // Eine gespeicherte Reise fuehrt nicht zu einem Punkt, sondern zurueck in
  // die Routenplanung.
  onReise: (reise) => {
    route.restore(reise);
    showView('route');
  }
});

/**
 * Palettenwechsel lädt den Style neu. `diff: true` hält die Kacheln im Speicher,
 * aber Sichtbarkeiten und eigene Ebenen (Route) sind danach weg - beides wird
 * hier wieder aufgetragen.
 */
let renderedPalette = 'farbig';
function usePalette(palette) {
  if (!palette || palette === renderedPalette) return;
  renderedPalette = palette;
  map.setStyle(buildStyle({ palette }), { diff: true });
  map.once('styledata', () => {
    filters.reapply();
    route.redraw();
    tracks.redraw();
    search.redraw();
    radar.reapply();
    relief3d.reapply();
  });
}

const filters = createFilterPanel({
  map,
  onChange: () => savePrefsSoon(),
  onPalette: usePalette,
  // "Alle zeigen" im Filterfeld räumt auch das Suchfeld auf.
  onKategorieWeg: () => search?.leeren()
});

// Zielfuehrung blickt schraeg nach vorn - ohne Gelaende wirkt das wie eine
// umgelegte Tapete. Danach den vorherigen Zustand wiederherstellen.
let dreiDVorher = false;
const navOverlay = createNavOverlay({
  map,
  onStart: () => {
    dreiDVorher = relief3d.istAn();
    relief3d.setOn(true, { stillLassen: true });
  },
  onEnd: () => {
    relief3d.setOn(dreiDVorher, { stillLassen: true });
    setPanel(!compact());
  }
});

const route = createRoutePanel({
  map,
  // Immer aufschlagen, nie umschalten: hier wird gerade ein Punkt gesetzt.
  onActivate: () => info?.routeZeigen(),
  onStartNavigation: (auftrag) => {
    setPanel(false);
    navOverlay.starten(auftrag);
  },
  /**
   * Eine Reise liegt bei den Favoriten - dieselbe Ablage, derselbe Weg zum
   * Server. `lat`/`lng` sind der Start, damit die Marke in der Karte steht;
   * unter `reise` liegt alles, was die Routenplanung zum Wiederaufnehmen
   * braucht.
   */
  onSaveTrip: (reise) => favorites.add({
    name: `${reise.from.name || 'Start'} → ${reise.to.name || 'Ziel'}`,
    kind: 'Reise',
    lat: reise.from.lat,
    lng: reise.from.lng,
    reise
  })
});
/**
 * Das Niederschlagsradar liegt jetzt unter einem eigenen Schienen-Eintrag.
 * Laeuft es, steht das auch dann am Knopf, wenn die Spalte etwas anderes zeigt
 * - sonst sucht man den Regen in den Einstellungen.
 */
const radar = createRadar({
  map,
  onZustand: (an) => {
    const marke = el('meteo-state');
    if (marke) marke.hidden = !an;
  }
});
/**
 * Die Ortsprognose steht unter dem Radar in derselben Spalte: Radar sagt, wo
 * es gerade regnet, die Prognose, wie die Woche wird.
 */
const ortsprognose = createMeteoPanel({ map });
const relief3d = createRelief3d({ map });

// Beim Setzen eines Routenpunkts aus der Karte heraus nicht sofort zuklappen -
// die Liste der Verbindungen ist ja das Ergebnis.

let prefsTimer;
function savePrefsSoon() {
  clearTimeout(prefsTimer);
  prefsTimer = setTimeout(async () => {
    try {
      await backend.prefs.save(account.getProfile(), { filters: filters.getState() });
    } catch { /* Einstellungen sind nicht kritisch */ }
  }, 600);
}

async function loadPrefs() {
  try {
    const prefs = await backend.prefs.load(account.getProfile());
    if (prefs?.filters) filters.setState(prefs.filters);
  } catch { /* Standardfilter behalten */ }
}

radar.mount(el('radar'));
ortsprognose.mount(el('ortsprognose'));
relief3d.mount(el('relief3d'));

filters.mount({
  basemapHost: el('basemap'),
  groupHost: el('layer-groups'),
  themeHost: el('poi-themes'),
  nameInput: el('poi-name'),
  resetBtn: el('btn-reset'),
  poiNoneBtn: el('btn-poi-none')
});

route.mount({
  modeHost: el('route-modes'),
  fromInput: el('route-from'),
  toInput: el('route-to'),
  swapBtn: el('route-swap'),
  hereFrom: el('route-here-from'),
  hereTo: el('route-here-to'),
  clearBtn: el('route-clear'),
  result: el('route-result'),
  whenBox: el('route-when'),
  dateInput: el('route-date'),
  timeInput: el('route-time'),
  depBtn: el('route-dep'),
  arrBtn: el('route-arr'),
  nowBtn: el('route-now'),
  veloBox: el('route-velo'),
  veloTyp: el('route-velotyp'),
  veloHuegel: el('route-velo-huegel'),
  veloVerkehr: el('route-velo-verkehr'),
  veloBelag: el('route-velo-belag')
});

/**
 * Touren: aufzeichnen, wiedersehen, als GPX weitergeben.
 */
const tracks = createTracksPanel({
  map,
  backend,
  getProfile: () => account.getProfile(),
  onAktiv: () => info?.tourenZeigen(),
  onNavigate: collapseOnCompact
});
tracks.mount({
  aufnahme: el('tour-rec'),
  liste: el('tour-list'),
  badge: el('tour-count'),
  importBtn: el('tour-import'),
  importInput: el('tour-file')
});

/**
 * Zuletzt angesehen - die Liste unten in der Schiene. Ein Klick darauf fliegt
 * hin und schlaegt dieselbe Auskunft wieder auf.
 */
const recents = createRecents({
  host: el('recent-list'),
  onPick: (ort) => {
    map.flyTo({ center: [ort.lng, ort.lat], zoom: Math.max(map.getZoom(), 15), duration: 700 });
    info?.zeigeTreffer({ name: ort.name, lat: ort.lat, lng: ort.lng, klasse: ort.klasse });
    collapseOnCompact();
  }
});

info = createInfoPopup({
  map,
  onFavorite: (fav) => favorites.add(fav),
  onRoutePoint: (which, point) => route.setPoint(which, point),
  onBesucht: (ort) => recents.merken(ort),
  /* Der Knopf in der Schiene leuchtet, solange seine Ansicht offen ist - und
     kommt Inhalt, faehrt die Spalte auf, auch wenn sie eingeklappt war. */
  onAnsicht: (welche) => {
    routeBtn.classList.toggle('is-active', welche === 'route');
    favBtn.classList.toggle('is-active', welche === 'favoriten');
    tourBtn.classList.toggle('is-active', welche === 'touren');
    meteoBtn.classList.toggle('is-active', welche === 'meteo');
    if (welche) setSidebar(true);
  }
});

const search = createSearchBox({
  map,
  input: el('search-input'),
  clearBtn: el('search-clear'),
  resultList: el('search-results'),
  // Ein Suchtreffer soll nicht nur anfliegen, sondern auch gleich erzaehlen,
  // was er ist - Marke in der Karte, Auskunft daneben.
  onPick: (treffer) => {
    collapseOnCompact();
    info?.zeigeTreffer(treffer);
  },
  onKategorie: (kat) => filters.setKategorie(kat)
});

(async () => {
  await account.restore();
  await favorites.load();
  await tracks.load();
  await loadPrefs();
  // In der App einmal die Ortung freischalten. Im Browser tut das nichts -
  // dort fragt der Browser selbst, sobald jemand den Standortknopf drückt.
  await ortungVorbereiten();
})();

map.on('error', (e) => {
  // Reliefkacheln decken nur die Schweiz ab – fehlende Kacheln sind normal.
  const url = e?.error?.url || '';
  if (url.includes('wmts.geo.admin.ch')) return;
  console.warn('Kartenfehler:', e?.error?.message || e);
});

document.addEventListener('keydown', (e) => {
  if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
    e.preventDefault();
    el('search-input').focus();
  }
});

/**
 * Eine Lage zurück.
 *
 * Die Reihenfolge ist die, in der die Dinge übereinander liegen: zuerst die
 * Trefferliste der Suche, dann die Darstellungsspalte, dann die Seitenspalte
 * mit Route, Favoriten, Touren, Wetter oder Auskunft. Erst wenn davon nichts
 * offen ist, gibt `false` zurück und der Aufrufer entscheidet - in der App
 * heisst das: in den Hintergrund, nicht beenden.
 *
 * Die Zielführung steht über allem und wird nicht beiläufig beendet. Ein
 * Druck kündigt es an, der zweite führt es aus: unterwegs ist die
 * Zurück-Geste schnell einmal ausgelöst, und eine abgebrochene Führung
 * mitten auf der Strasse ist ärgerlicher als ein zusätzlicher Druck.
 */
let fuehrungAbbruchBis = 0;

function eineLageZurueck() {
  if (navOverlay.laeuft()) {
    if (Date.now() < fuehrungAbbruchBis) {
      fuehrungAbbruchBis = 0;
      navOverlay.beenden();
    } else {
      fuehrungAbbruchBis = Date.now() + 3000;
      toast('Nochmals zurück beendet die Führung.');
    }
    return true;
  }
  if (!el('search-results').hidden) {
    search.hide();
    return true;
  }
  if (!panel.hidden) {
    setPanel(false);
    return true;
  }
  if (offeneAnsicht()) {
    info?.schliessen();
    return true;
  }
  return false;
}

zurueckTaste(eineLageZurueck);
// Wie bei `window.map`: im Dev-Bau greifbar, damit sich die Abfolge ohne
// Android-Gerät prüfen lässt.
if (import.meta.env?.DEV) window.zurueck = eineLageZurueck;

// Bewusst nur fuer Android: am Rechner schliesst Esc schon an drei Stellen
// einzeln (Suchliste, Auskunft, Darstellungsspalte). Noch ein Zuhoerer
// obendrauf schloesse zwei Lagen auf einmal.
